import { analyticsOptOutEnabled } from "./analytics-privacy";

export const CAMPAIGN_FIELDS = [
  ["utm_source", "campaign_source"],
  ["utm_medium", "campaign_medium"],
  ["utm_campaign", "campaign_name"],
  ["utm_content", "campaign_content"],
  ["utm_term", "campaign_term"],
  ["utm_id", "campaign_id"],
  ["utm_source_platform", "utm_source_platform"],
] as const;

type CampaignKey = (typeof CAMPAIGN_FIELDS)[number][0];
export type Campaign = Partial<Record<CampaignKey, string>>;
type Touch = { campaign: Campaign; at: number };
type Attribution = { first: Touch; latest: Touch };

const STORAGE_KEY = "analytics:campaignAttribution:v1";
const ENTRY_KEY = "analytics:campaignEntry:v1";
const SIGNUP_COOKIE = "limrun_signup_attribution";
const MAX_AGE_MS = 90 * 24 * 60 * 60 * 1000;
const ENTRY_MAX_AGE_MS = 30 * 60 * 1000;
let inMemory: Attribution | undefined;

export function clearCampaignAttribution(): void {
  inMemory = undefined;
  try { localStorage.removeItem(STORAGE_KEY); } catch { /* Storage can be unavailable. */ }
  try { sessionStorage.removeItem(ENTRY_KEY); } catch { /* Storage can be unavailable. */ }
  writeSignupCookie();
}

// The API needs these labels before it creates the organization and emits signup.
// Every login callback lives under /authn, so other requests never carry them.
function writeSignupCookie(attribution?: Attribution): void {
  const hostname = window.location.hostname;
  // Production and staging each share one parent domain with their API.
  const domain = ["limrun.com", "limrun.dev"].find((parent) => hostname === parent || hostname.endsWith(`.${parent}`));
  if (!domain) return;
  const value = attribution ? encodeURIComponent(JSON.stringify(attribution, (key, value) => key === "utm_term" ? undefined : value)) : "";
  try {
    document.cookie = `${SIGNUP_COOKIE}=${value}; Domain=${domain}; Path=/authn; Max-Age=${attribution ? MAX_AGE_MS / 1000 : 0}; SameSite=Lax; Secure`;
  } catch { /* Missing browser storage must never prevent authentication. */ }
}

function campaignFrom(values: Record<string, unknown>): Campaign | undefined {
  const campaign: Campaign = {};
  for (const [key] of CAMPAIGN_FIELDS) {
    const value = values[key];
    const invalid = key === "utm_term" ? /[^a-z0-9._ -]/i : /[^a-z0-9._-]/i;
    if (typeof value === "string" && value.length >= 1 && value.length <= 100 && !invalid.test(value)) {
      const normalized = key === "utm_term" ? value.trim() : value;
      if (normalized) campaign[key] = normalized;
    }
  }
  // Keep each ad's labels together; never fill a partial new campaign from an older ad.
  return campaign.utm_source && campaign.utm_medium && campaign.utm_campaign ? campaign : undefined;
}

function touchFrom(value: unknown): Touch | undefined {
  if (!value || typeof value !== "object") return;
  const touch = value as Partial<Touch>;
  if (typeof touch.at !== "number" || !Number.isFinite(touch.at) || touch.at > Date.now() || Date.now() - touch.at >= MAX_AGE_MS) return;
  if (!touch.campaign || typeof touch.campaign !== "object") return;
  const campaign = campaignFrom(touch.campaign);
  return campaign ? { campaign, at: touch.at } : undefined;
}

function readAttribution(): Attribution | undefined {
  if (analyticsOptOutEnabled()) {
    clearCampaignAttribution();
    return;
  }
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const saved = raw ? JSON.parse(raw) : inMemory;
    const latest = touchFrom(saved?.latest);
    const first = touchFrom(saved?.first) ?? latest;
    inMemory = first && latest ? { first, latest } : undefined;
  } catch {
    const latest = touchFrom(inMemory?.latest);
    const first = touchFrom(inMemory?.first) ?? latest;
    inMemory = first && latest ? { first, latest } : undefined;
  }
  return inMemory;
}

// Run before authentication redirects remove the advertising query string.
export function captureCampaignArrival(): void {
  const saved = readAttribution();
  if (analyticsOptOutEnabled()) return;
  const campaign = campaignFrom(Object.fromEntries(new URLSearchParams(window.location.search)));
  if (!campaign) {
    writeSignupCookie(saved);
    return;
  }
  const latest = { campaign, at: Date.now() };
  inMemory = { first: saved?.first ?? latest, latest };
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(inMemory)); } catch { /* Keep same-page attribution when storage is blocked. */ }
  writeSignupCookie(inMemory);
  const query = new URLSearchParams(window.location.search);
  const clickIds: Record<string, string> = {};
  for (const key of ["gclid", "gbraid", "wbraid"]) {
    const value = query.get(key);
    if (value && value.length <= 500 && !/[^a-z0-9_-]/i.test(value)) clickIds[key] = value;
  }
  try { sessionStorage.setItem(ENTRY_KEY, JSON.stringify({ ...latest, clickIds })); } catch { /* The current URL remains available. */ }
}

// Forward only this browser session's entry labels to Google, not old paid labels on later direct visits.
export function entryCampaignParameters(): Record<string, string> {
  if (analyticsOptOutEnabled()) {
    clearCampaignAttribution();
    return {};
  }
  let saved: { campaign?: Campaign; at?: number; clickIds?: Record<string, unknown> } | undefined;
  try { saved = JSON.parse(sessionStorage.getItem(ENTRY_KEY) ?? "null"); } catch { /* Use current URL below. */ }
  const query = new URLSearchParams(window.location.search);
  const current = campaignFrom(Object.fromEntries(query));
  const validSaved = saved?.at && saved.at <= Date.now() && Date.now() - saved.at < ENTRY_MAX_AGE_MS;
  const campaign = current ?? (validSaved && saved?.campaign ? campaignFrom(saved.campaign) : undefined);
  const values: Record<string, string> = { ...campaign };
  for (const key of ["gclid", "gbraid", "wbraid"]) {
    const value = query.get(key) ?? (validSaved ? saved?.clickIds?.[key] : undefined);
    if (typeof value === "string" && value.length >= 1 && value.length <= 500 && !/[^a-z0-9_-]/i.test(value)) values[key] = value;
  }
  return values;
}

export function latestCampaign(): Campaign {
  return readAttribution()?.latest.campaign ?? {};
}

export function campaignEventProperties(): Record<string, string> {
  const attribution = readAttribution();
  if (!attribution) return {};
  const properties: Record<string, string> = {};
  for (const [utm, alias] of CAMPAIGN_FIELDS) {
    const latest = attribution.latest.campaign[utm];
    const first = attribution.first.campaign[utm];
    if (latest) {
      properties[utm] = latest;
      properties[alias] = latest;
    }
    if (first) properties[`first_${utm}`] = first;
  }
  return properties;
}
